import { Divider, Flex, Spacer } from '@chakra-ui/react';
import { INVITE_MEMBER, SHARE, UPGRADE } from 'constants/mixpanel';
import { useDetectPhishing } from 'hooks/useDetectPhishing/useDetectPhishing';
import { PhishingShareMethod } from 'hooks/useDetectPhishing/useDetectPhishing.config';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { grantGroupInfographicPermission, revokeGroupInfographicPermission } from 'libs/e1api/permissionmanager';
import { requestInviteTeamMember, shareNotifyToTeamMembers, shareWithTeam, unShareWithTeam } from 'libs/e1api/team';
import { notifyToWorkspace } from 'libs/e1api/workspace';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectIsTeamSharingEnabled } from 'modules/Editor/store/editorSettingsSelector';
import { setTeamSharingEnabled } from 'modules/Editor/store/editorSettingsSlice';
import { UserTypes, useUser, useWorkspace } from 'modules/User';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { isStandalone } from 'utils/environment';

import { ShareUpgradeButton } from '../../ShareUpgradeButton';
import { UpgradeButton } from '../../UpgradeButton';
import { DefaultTabNumber, ProjectLinkType } from '../DownloadModal/DownloadModal.types';
import { useToastHelpers } from '../useToastHelpers';

/**
 * Section components
 */
import { InviteTeamMembers } from './InviteTeamMembers';
import { LinkSharing } from './LinkSharing';
import { InviteTeamMemberProps, SectionComponentsProps, SharePanelProps } from './SharePanel.types';
import { SharePreviewLink } from './SharePreviewLink';
import { SharePrivateLink } from './SharePrivateLink';
import { ShareProjectLink } from './ShareProjectLink';
import { ShareWithGroups } from './ShareWithGroups';
import { ShareWithOthers } from './ShareWithOthers';
import { ShareWithTeam } from './ShareWithTeam';

// Return the sections of the share panel via user type and role
const SectionComponents = ({
  isTeamSharingEnabled,
  onUpgrade,
  onClose,
  onCopyPrivateLink,
  onCopyCurrentLink,
  onNotifyTeamMember,
  onNotifyTeams,
  onTeamShareToggle,
  onInviteTeamMember,
  onEnterpriseShare,
  onEnterpriseUnShare,
  onChangeProjectLink,
  projectLink,
  tabIndex,
  inviteTeamMember,
  setInviteTeamMember,
}: SectionComponentsProps) => {
  const { id } = useParams();
  const {
    userId,
    userType,
    isFreeUser,
    isPremiumUser,
    isEducationUser,
    isBusinessUser,
    isEnterpriseUser,
    isTeamOwnerRole,
    isTeamMemberRole,
    isGuestRole,
  } = useUser();
  const { teams, userPermissions } = useWorkspace(id, isEnterpriseUser);
  const { venngageE2 } = useVenngageE1Gate();

  const gateFreeShareModalExp = venngageE2?.experiments?.freeUserShareModal;

  const isDesignOnwer = useMemo(
    () => userId && userPermissions?.owner.id && userId === userPermissions?.owner.id,
    [userId, userPermissions],
  );

  /**
   * Render sections regarding the user type and role on the SharePanel
   */
  if (!isStandalone) {
    if (isFreeUser || isGuestRole) {
      return (
        <>
          {tabIndex === DefaultTabNumber.Preview && <SharePreviewLink disabled onCopyLink={onCopyPrivateLink} />}
          {tabIndex === DefaultTabNumber.Share && (
            <>
              {gateFreeShareModalExp ? (
                <>
                  <InviteTeamMembers
                    disabled
                    onUpgrade={() => onUpgrade(UPGRADE.feature.Share)}
                    inviteTeamMember={inviteTeamMember}
                    setInviteTeamMember={setInviteTeamMember}
                  />
                  <ShareWithTeam disabled />
                  <Spacer />
                  <ShareProjectLink disabled value={projectLink} setValue={onChangeProjectLink} />
                  <Spacer />
                  <Spacer mt={2} />
                  <SharePrivateLink onCopyLink={onCopyCurrentLink} />
                  <Spacer mt={2} />
                  <ShareUpgradeButton userType={UserTypes.Premium} tabPanel={DefaultTabNumber.Share} />
                </>
              ) : (
                <>
                  <Spacer />
                  <ShareWithOthers onUpgrade={() => onUpgrade(UPGRADE.feature.Share)} />
                  <SharePrivateLink disabled onCopyLink={onCopyPrivateLink} />
                  <Divider mt='2' />
                  <InviteTeamMembers
                    disabled
                    addLeftMargin
                    addYMargin
                    onUpgrade={() => onUpgrade(UPGRADE.feature.DownloadBusiness)}
                    inviteTeamMember={inviteTeamMember}
                    setInviteTeamMember={setInviteTeamMember}
                  />
                  <ShareWithTeam disabled />
                  <Divider my='2' />
                  <UpgradeButton userType={userType} renderFrom='share-panel' />
                </>
              )}
            </>
          )}
        </>
      );
    }

    if (isPremiumUser) {
      return (
        <>
          <Spacer />
          <SharePrivateLink onCopyLink={onCopyPrivateLink} />
          <Divider mt='2' />
          <InviteTeamMembers
            disabled
            inviteTeamMember={inviteTeamMember}
            setInviteTeamMember={setInviteTeamMember}
            onUpgrade={() => onUpgrade(UPGRADE.feature.DownloadBusiness)}
          />
          <ShareWithTeam disabled />
        </>
      );
    }

    if (isBusinessUser) {
      return (
        <>
          <Spacer />
          <InviteTeamMembers
            disabled={!isTeamOwnerRole}
            hideLock={isTeamMemberRole}
            onUpgrade={() => onUpgrade(UPGRADE.feature.Share)}
            onInvite={onInviteTeamMember}
            inviteTeamMember={inviteTeamMember}
            setInviteTeamMember={setInviteTeamMember}
          />
          <ShareWithTeam
            enableTeamShare={isTeamSharingEnabled}
            onChange={onTeamShareToggle}
            onCopyLink={onCopyCurrentLink}
            onNotify={onNotifyTeamMember}
            onClose={onClose}
          />
          <Spacer />
          <Divider my='2' />
          <LinkSharing onCopyLink={onCopyPrivateLink}></LinkSharing>
        </>
      );
    }

    if (isEducationUser) {
      return (
        <>
          <Spacer />
          <SharePrivateLink onCopyLink={onCopyPrivateLink} />
          <ShareWithTeam
            mode={'education'}
            enableTeamShare={isTeamSharingEnabled}
            onChange={onTeamShareToggle}
            onCopyLink={onCopyCurrentLink}
            onClose={onClose}
          />
        </>
      );
    }

    if (isEnterpriseUser) {
      return (
        <>
          <Spacer />
          <ShareWithGroups
            disabled={!isDesignOnwer}
            teams={teams}
            userPermissions={userPermissions}
            onShareClick={onEnterpriseShare}
            onUnShareClick={onEnterpriseUnShare}
            onCopyLink={onCopyCurrentLink}
            onNotify={onNotifyTeams}
          />
          <Spacer />
          <Divider my='2' />
          <LinkSharing onCopyLink={onCopyPrivateLink}></LinkSharing>
        </>
      );
    }
  }

  // For default && standalone env
  return (
    <>
      <Spacer />
      <LinkSharing onCopyLink={onCopyPrivateLink}></LinkSharing>
    </>
  );
};

/**
 * Contorller component for the SharePanel
 */
export const SharePanel = memo(({ onUpgrade = () => {}, onClose = () => {}, tabIndex }: SharePanelProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('common_navbar');
  const isTeamSharingEnabled = useAppSelector(selectIsTeamSharingEnabled);
  const { id } = useParams();
  const { venngageE2 } = useVenngageE1Gate();
  const { userType, isEnterpriseUser } = useUser();
  const { revalidate: revalidateWorkspace } = useWorkspace(id, isEnterpriseUser);
  const { detectPhishing } = useDetectPhishing();
  const { copyTextToClipboard, showFailedToast, showSuccessToast } = useToastHelpers({
    successToast: t('modals.share-panel.toast.success-copy-link'),
    failToast: t('modals.share-panel.toast.failure-copy-link'),
  });
  const [projectLink, setProjectLink] = useState<string>(ProjectLinkType.View);
  const [inviteTeamMember, setInviteTeamMember] = useState<InviteTeamMemberProps>({ status: false, errType: '' });

  // Generate the private link and copy it to clipboard
  const handleCopyPrivateLink = useCallback(() => {
    const shareLinkId = isStandalone ? id : venngageE2?.privateLinkId;
    if (!!shareLinkId) {
      const plPageUrl = `${window.location.protocol}//${window.location.host}/pl/${shareLinkId}`;

      copyTextToClipboard(plPageUrl);
      detectPhishing(PhishingShareMethod.CopyLink);

      // Mixpanel event
      Mixpanel.track(SHARE.eventName, {
        'User Plan': userType,
        share_method: 'Private Link',
        from: 'Editor Top Menu',
        Location: 'Share Panel',
      });
    } else {
      showFailedToast(
        t('modals.share-panel.toast.failure-copy-link'),
        t('modals.share-panel.toast.failure-copy-link-description'),
      );
    }
  }, [id, userType, copyTextToClipboard, showFailedToast, t, venngageE2, detectPhishing]);

  // Generate the curernt editor link and copy it to clipboard
  const handleCopyCurrentLink = useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      const link = window.location.href;
      copyTextToClipboard(link);

      // Mixpanel event
      const isPostShareModal = event.currentTarget?.dataset?.testid === 'share-with-team-invite-modal-copylink-button';
      Mixpanel.track(SHARE.eventName, {
        'User Plan': userType,
        share_method: 'Edit Link',
        from: isPostShareModal ? 'Editor post share modal' : 'Editor Top Menu',
        Location: 'Share Panel',
      });
    },
    [userType, copyTextToClipboard],
  );

  // Invite the new team member using the email address
  const handleInviteTeamMember = useCallback(
    async (email: string, done = () => {}) => {
      const { status, errType } = await requestInviteTeamMember(email);
      if (status) {
        setInviteTeamMember({ status: false, errType: '' });
        showSuccessToast(t('modals.share-panel.toast.success-invite-team-member', { email }));
      } else {
        setInviteTeamMember({ status: true, errType: errType });
        showFailedToast(t('modals.share-panel.toast.failure-invite-team-member'));
      }

      // Mixpanel event
      Mixpanel.track(INVITE_MEMBER.eventName, {
        'User Plan': userType,
        member_type: 'member = paid',
        'Button ID': 'Editor',
        'User email': email,
      });

      if (done) done();
    },
    [userType, showSuccessToast, showFailedToast, t],
  );

  // Notify message to the team members
  const handleNotifyTeamMember = useCallback(
    async (message: string) => {
      if (!id) return;

      const result = await shareNotifyToTeamMembers(id, message);

      if (result.status === 'ok') {
        showSuccessToast(t('modals.share-panel.toast.success-notify'));

        Mixpanel.track(SHARE.eventName, {
          'User Plan': userType,
          share_method: 'Email Team',
          from: 'Editor post share modal',
          Location: 'Share Panel',
        });
      } else {
        showFailedToast(t('modals.share-panel.toast.failure-notify'));
      }
    },
    [id, userType, showSuccessToast, showFailedToast, t],
  );

  // Notify message to the team members
  const handleNotifyTeams = useCallback(
    async (message: string, teamIds: string[]) => {
      if (!id) return;

      const result = await notifyToWorkspace(id, message, teamIds);

      if (result.status === 'ok') {
        showSuccessToast(t('modals.share-panel.toast.success-notify'));

        Mixpanel.track(SHARE.eventName, {
          'User Plan': userType,
          share_method: 'Email Team',
          from: 'Editor post share modal',
          Location: 'Share Panel',
        });
      } else {
        showFailedToast('Failed to send email');
      }
    },
    [id, userType, showFailedToast, showSuccessToast, t],
  );

  // Update the team share state using E1 API
  const handleTeamShareToggle = useCallback(async () => {
    if (!id) return;

    try {
      isTeamSharingEnabled ? await unShareWithTeam(id) : await shareWithTeam(id);

      // Trigger Mixpanel event when team sharing switch is on
      if (!isTeamSharingEnabled) {
        Mixpanel.track(SHARE.eventName, {
          'User Plan': userType,
          share_method: 'With Team',
          from: 'Editor Top Menu',
          Location: 'Share Panel',
        });
      }

      // Turn on/off RTC
      dispatch(setTeamSharingEnabled(!isTeamSharingEnabled));
    } catch (error) {
      showFailedToast(t('modals.share-panel.toast.failure-team-share'));
    }
  }, [id, isTeamSharingEnabled, userType, showFailedToast, dispatch, t]);

  // Grant the permission for sharing this infographic using the team id list
  const handleEnterpriseShare = useCallback(
    async (teamIds: string[], done = () => {}) => {
      if (!id) return;

      teamIds.forEach(async (teamId, index, arr) => {
        await grantGroupInfographicPermission(id, teamId);

        const isLast = index === arr.length - 1;
        if (isLast) {
          if (revalidateWorkspace) await revalidateWorkspace();
          if (done) done();

          // Turn on/off RTC
          dispatch(setTeamSharingEnabled(true));
        }
      });
    },
    [id, revalidateWorkspace, dispatch],
  );

  // Revoke the permission for sharing by the team id
  const handleEnterpriseUnShare = useCallback(
    async (teamId: string, done = () => {}) => {
      if (!id) return;

      await revokeGroupInfographicPermission(id, teamId);

      if (revalidateWorkspace) await revalidateWorkspace();
      if (done) {
        done();
      }
      // Turn off RTC
      dispatch(setTeamSharingEnabled(false));
    },
    [id, revalidateWorkspace, dispatch],
  );

  const handleChangeProjectLink = (value: string) => {
    setProjectLink(value);
  };

  const sharePanelTestId = 'share-panel';

  return (
    <Flex flexDir={'column'} w='full' gap='2' justifyContent={'space-between'} data-testid={sharePanelTestId}>
      <SectionComponents
        isTeamSharingEnabled={isTeamSharingEnabled}
        onUpgrade={onUpgrade}
        onClose={onClose}
        onCopyPrivateLink={handleCopyPrivateLink}
        onCopyCurrentLink={handleCopyCurrentLink}
        onNotifyTeamMember={handleNotifyTeamMember}
        onNotifyTeams={handleNotifyTeams}
        onTeamShareToggle={handleTeamShareToggle}
        onInviteTeamMember={handleInviteTeamMember}
        onEnterpriseShare={handleEnterpriseShare}
        onEnterpriseUnShare={handleEnterpriseUnShare}
        onChangeProjectLink={handleChangeProjectLink}
        projectLink={projectLink}
        tabIndex={tabIndex}
        inviteTeamMember={inviteTeamMember}
        setInviteTeamMember={setInviteTeamMember}
      />
    </Flex>
  );
});
SharePanel.displayName = 'SharePanel';
